import { z } from "zod";

export const walletSchema = z.object({
  id: z.string(),
  userId: z.string(),
  balance: z.number().int().nonnegative(),
  updateAt: z.string().or(z.date()),
});

export type Wallet = z.infer<typeof walletSchema>;

export const withdrawalSchema = z.object({
  id: z.string(),
  reference: z.string(),
  amount: z.number().int().positive(),
  // The DB stores lowercase (pending/accepted/rejected); normalize to uppercase for consistent UI handling
  status: z
    .string()
    .transform((v) => v.toUpperCase() as "PENDING" | "ACCEPTED" | "REJECTED")
    .pipe(z.enum(["PENDING", "ACCEPTED", "REJECTED"])),
  mean: z.enum(["MOBILE_MONEY", "BANK_TRANSACTION"]),
  phoneMomo: z.string().nullish(),
  bankNumber: z.string().nullish(),
  demandDate: z.string().or(z.date()),
  decisionDate: z.string().or(z.date()).nullish(),
  reason: z.string().nullish(),
});

export type Withdrawal = z.infer<typeof withdrawalSchema>;

// Backend returns { page: totalPageCount, data: [] }
// 'page' in this response is the TOTAL NUMBER OF PAGES (not the current page)
export const paginatedWithdrawalSchema = z.object({
  page: z.number().int().nonnegative(), // total page count
  data: z.array(withdrawalSchema),
});

export type PaginatedWithdrawal = z.infer<typeof paginatedWithdrawalSchema>;

export const withdrawalRequestSchema = z
  .object({
    // Saisi dans la devise du compte, converti en FCFA à l'envoi.
    amount: z.number({ invalid_type_error: "Montant invalide" }).positive("Montant invalide"),
    mean: z.enum(["MOBILE_MONEY", "BANK_TRANSACTION"]),
    phoneMomo: z.string().optional(),
    bankNumber: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.mean === "MOBILE_MONEY" && !data.phoneMomo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Le numéro de téléphone est requis",
        path: ["phoneMomo"],
      });
    }
    if (data.mean === "BANK_TRANSACTION" && !data.bankNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Le numéro de compte est requis",
        path: ["bankNumber"],
      });
    }
  });

export type WithdrawalRequest = z.infer<typeof withdrawalRequestSchema>;
